import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { usersApi } from "@/lib/humhub-api/users";

interface UseImageUploadReturn {
  // プロフィール画像
  uploadProfileImage: (file: File) => Promise<ImageUploadResponse>;
  isUploadingProfile: boolean;
  profileError: string | null;
  resetProfileError: () => void;

  // バナー画像
  uploadBannerImage: (file: File) => Promise<ImageUploadResponse>;
  isUploadingBanner: boolean;
  bannerError: string | null;
  resetBannerError: () => void;
}

interface ImageUploadResponse {
  success: boolean;
  image: {
    url: string;
    user_id: number;
  };
}

export const useImageUpload = (): UseImageUploadReturn => {
  const { t } = useTranslation();

  const validateImage = useCallback(
    (file: File): string | null => {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (!allowedTypes.includes(file.type)) {
        return (
          t("common.fileExtensionError") ||
          "Invalid file type. Please select PNG, JPEG, or JPG files."
        );
      }
      if (file.size > maxSize) {
        return t("common.fileSizeError") || "File size must be less than 5MB.";
      }
      return null;
    },
    [t],
  );

  const profileImageMutation = useMutation<ImageUploadResponse, Error, File>({
    mutationFn: async (file: File) => {
      const err = validateImage(file);
      if (err) throw new Error(err);
      return usersApi.uploadProfileImage(file);
    },
  });

  const bannerImageMutation = useMutation<ImageUploadResponse, Error, File>({
    mutationFn: async (file: File) => {
      const err = validateImage(file);
      if (err) throw new Error(err);
      return usersApi.uploadBannerImage(file);
    },
  });

  return {
    uploadProfileImage: (file) => profileImageMutation.mutateAsync(file),
    isUploadingProfile: profileImageMutation.isPending,
    profileError: profileImageMutation.error?.message || null,
    resetProfileError: () => profileImageMutation.reset(),

    uploadBannerImage: (file) => bannerImageMutation.mutateAsync(file),
    isUploadingBanner: bannerImageMutation.isPending,
    bannerError: bannerImageMutation.error?.message || null,
    resetBannerError: () => bannerImageMutation.reset(),
  };
};
